import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TicketRepository } from '../infrastructure/repositories/ticket.repository';
import { TicketSearchPattern } from '../model/enums/ticket-search-pattern';
import { TicketInstance } from '../model/interfaces/ticket-instance';
import { TicketWinStatus } from '../model/interfaces/ticket-win-status';
import { TicketWinEvent } from '../model/ticket-win-event';

import { Injectable } from '@angular/core';
import { TicketSearchResultGroup } from '../+shop-purchase/components/ticket-search-result-list.component/ticket-search-result-list.component';
import { MyTicketResponse } from '../model/interfaces/my-ticket-response';
import { Order } from '../model/interfaces/order';
import { Share } from '../model/interfaces/share';
import { TicketOwnership } from '../model/interfaces/ticket-ownership';
import { TicketPackage } from '../model/interfaces/ticket-package';
import { TicketPackageInstance } from '../model/interfaces/ticket-package-instance';
import { TicketReservation } from '../model/interfaces/ticket-reservation';
import { ResourceService } from './resource.service';

@Injectable()
export class TicketService
{
    private cache: any = {};
    private myTicketSubject: BehaviorSubject<MyTicketResponse>;
    private _ticketInstanceGroups: TicketSearchResultGroup[];

    private readonly resourceService: ResourceService;
    private readonly ticketRepository: TicketRepository;

    public constructor(
        resourceService: ResourceService,
        ticketRepository: TicketRepository
    )
    {
        this.resourceService = resourceService;
        this.ticketRepository = ticketRepository;
    }

    public getWinStatus(ticketNumber: number, numberOfDraws: number): Observable<TicketWinStatus>
    {
        const cacheKey = `${ticketNumber}-${numberOfDraws}`;
        const winStatus: TicketWinStatus = this.cache[cacheKey] as TicketWinStatus;
        if (winStatus != null)
        {
            return of(winStatus);
        }

        // Send empty ticketLetter for now to get full history not limited to ownership.
        // TODO: Remove this when we implement logic to differentiate among ownerships in the history.

        // return this.ticketRepository.getWinStatus(ticketNumber, ticketLetter, numberOfDraws).pipe(
        return this.ticketRepository.getWinStatus(ticketNumber, numberOfDraws).pipe(
            map((ws) =>
            {
                this.cache[cacheKey] = ws;
                return ws;
            })
        );
    }

    public getFmtOpenState(ticketNumber: number, ticketLetter: string): Observable<boolean>
    {
        return this.ticketRepository.getTicketsForFmt(ticketNumber, ticketLetter).pipe(map(tickets => {
            if(tickets == null || tickets.length == 0)
            {
                return null;
            }
            var share = tickets[0].Share;
            return share.Ticket.TicketNumber === ticketNumber && share.Name === ticketLetter;
        }));
    }

    public getTicketsForFmt(): Observable<TicketOwnership[]>
    {
        return this.ticketRepository.getTicketsForFmt();
    }

    public getWinStatusByPlayer(): Observable<TicketWinEvent[]>
    {
        return this.ticketRepository.getWinStatusByPlayer();
    }

    public getTicketInstances(ticketNumber: number): Observable<ReadonlyArray<TicketInstance>>
    {
        return this.ticketRepository.getTicketInstances(ticketNumber);
    }

    public getSVKByTicket(ticketNumber: number, ticketLetter: string): Observable<string>
    {
        const cacheKey = `${ticketNumber}-${ticketLetter}`;

        const svk = this.cache[cacheKey] as string;
        if (svk != null)
        {
            return of(svk);
        }

        return this.ticketRepository.getSVKByTicket(ticketNumber, ticketLetter).pipe(
            map((_svk) => { this.cache[cacheKey] = _svk; return _svk; })
        );
    }

    public searchTickets(query: string, lotteryClassId: number, pattern: TicketSearchPattern): Observable<ReadonlyArray<TicketInstance>>
    {
        return this.ticketRepository.searchTickets(query, lotteryClassId, pattern);
    }

    public getTicketInstanceGroups(): TicketSearchResultGroup[]
    {
        return this._ticketInstanceGroups;
    }

    public setTicketInstanceGroups(value: TicketSearchResultGroup[]): void
    {
        this._ticketInstanceGroups = value;
    }

    public getTicketReservation(): Observable<TicketReservation[]>
    {
        return this.ticketRepository.getTicketReservation();
    }

    public getWinChanceByShares(shares: Share[], seed: number): number
    {
        if (shares.length === 0 && seed === 0)
        {
            return 0;
        }

        const uniqueTicketCount = (Array.from(new Set(shares.map(share => share.Ticket.TicketNumber)))).length + seed;
        const winChance: number = parseFloat(this.resourceService.getString(`shop_ticket_win_chance_${(uniqueTicketCount < 10 ? uniqueTicketCount : 'xx')}`));
        return winChance;
    }

    public getWinChanceByPackageContent(ticketPackage: TicketPackage): number
    {
        const contents = ticketPackage.TicketPackageContents;
        const ticketCount = contents.reduce((acc, previous) =>
        {
            return acc + previous.NumberOfTickets;
        }, 0);
        const winChance = parseFloat(this.resourceService.getString(`shop_ticket_win_chance_${ticketCount}`));
        return winChance;
    }



    public getWinChanceByOrder(order: Order): number
    {
        if (!order)
        {
            return 0;
        }

        const packageInstances = order.TicketPackageInstances;
        const sharesInPackages = packageInstances.reduce((accumulator: Share[], packageInstance: TicketPackageInstance) =>
        {
            return [...accumulator, ...packageInstance.Shares];
        }, []);

        const totalShares = order.Shares.concat(sharesInPackages);
        const winChance = this.getWinChanceByShares(totalShares, 0);
        return winChance;
    }
}
